import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { ENV, ENVIRONMENT, SENTRY_DSN } from 'config';
import 'config/moment';
import 'font-proxima-nova/style.css';
import 'react-datepicker/dist/react-datepicker.css';
import { createRoot } from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { scrubHubCallbackEvent, stashCallbackSearch } from 'services/hub-callback';
import { installSessionInterceptor, isSessionEvent, scrubSessionEvent } from 'services/session';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from 'styles/global';
import Theme, { MuiTheme } from 'styles/theme';
import App from './components/App';

const root = createRoot(document.getElementById('root') as HTMLElement);

// Before Sentry starts, so it never records the Hub's code and state.
stashCallbackSearch();

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      // A session event must never flush a replay: it would carry patient screens.
      Sentry.replayIntegration({ beforeErrorSampling: (event) => !isSessionEvent(event) }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    beforeSend(event) {
      // Log all errors to console for development
      if (ENVIRONMENT.isDev) {
        console.error('Sentry captured error:', event);
        return null;
      }

      return scrubHubCallbackEvent(scrubSessionEvent(event));
    },
    // The page load may still be recorded with the Hub's code and state.
    beforeSendTransaction(event) {
      return scrubHubCallbackEvent(event);
    },
    // Capture console errors as breadcrumbs
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level === 'error') {
        return null; // avoiding console errors
      }

      return breadcrumb;
    },
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason);
  });
}

// The default instance is used directly by AuthService and a few legacy callers.
installSessionInterceptor(axios);

root.render(
  <ThemeProvider theme={Theme}>
    <MuiThemeProvider theme={MuiTheme}>
      <GlobalStyle />
      <ToastContainer
        position='top-center'
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
      />
      <App />
    </MuiThemeProvider>
  </ThemeProvider>
);
